import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProducts, addProduct, updateProduct, uploadProductImages, uploadIntroImage } from './services/firebaseService';
import Header from './Header';
import Footer from './Footer';
import AdminSidebar from './AdminSidebar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';

const fieldLabelClass = 'text-xs uppercase tracking-wider text-gray-500 font-semibold';
const fieldInputClass =
  'border border-gray-200 rounded p-2 bg-white text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm w-full';
const fieldSelectClass = `${fieldInputClass} appearance-none pr-8`;

const AddEditProducts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    normalPrice: '',
    price: '',
    weight: '',
    category: '',
    subCategory: '',
    description: '',
    shopeeUrl: '',
    tiktokUrl: '',
    status: 'enable'
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [introImageFile, setIntroImageFile] = useState(null);
  const [introImagePreview, setIntroImagePreview] = useState('');
  const introImageInputRef = useRef(null);
  const productImagesInputRef = useRef(null);

  const categories = [
    'Welding Machines',
    'Accessories',
    'Power Tools',
    'Generator',
    'Air Compressors',
    'Home Improvements',
    'Others',
    'Machinery Service Consultant'
  ];

  const categoryTranslationKeys = {
    'Welding Machines': 'products.weldingMachines',
    Accessories: 'products.accessories',
    'Power Tools': 'products.powerTools',
    Generator: 'products.generator',
    'Air Compressors': 'products.airCompressors',
    'Home Improvements': 'products.homeImprovements',
    Others: 'products.promotions',
    'Machinery Service Consultant': 'products.machineryService'
  };

  const getCategoryLabel = (category) => t(categoryTranslationKeys[category] || category);

  // Sub-categories mapping
  const subCategories = {
    'Welding Machines': ['MIG', 'ARC', 'TIG', 'Plasma Cutter', 'Other'],
    'Power Tools': ['Amaxx', 'Dong Cheng', 'Other'],
    'Home Improvements': ['Karcher', 'Other']
  };

  // Get available sub-categories for selected category
  const getAvailableSubCategories = () => {
    return subCategories[formData.category] || [];
  };

  // Handle category change - reset sub-category when category changes
  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setFormData(prev => ({
      ...prev,
      category: newCategory,
      subCategory: '' // Reset sub-category when category changes
    }));
  };

  const loadProductForEdit = useCallback(async () => {
    setLoading(true);
    const result = await getProducts();
    if (result.success) {
      const product = result.products.find(p => p.id === id);
      if (product) {
        setFormData({
          name: product.name,
          sku: product.sku || '',
          normalPrice:
            product.normalPrice !== undefined && product.normalPrice !== null && product.normalPrice !== ''
              ? String(product.normalPrice)
              : '',
          price: product.price !== undefined && product.price !== null ? String(product.price) : '',
          weight: product.weight !== undefined && product.weight !== null 
            ? String(product.weight)
            : '',
          category: product.category,
          subCategory: product.subCategory || '',
          description: product.description,
          shopeeUrl: product.shopeeUrl || '',
          tiktokUrl: product.tiktokUrl || '',
          status: product.status === 'disable' ? 'disable' : 'enable'
        });
        setIntroImagePreview(product.image || '');
        setImagePreviews(product.additionalImages || []);
      }
    }
    setLoading(false);
  }, [id]);

  // Load product data for editing
  useEffect(() => {
    if (isEdit && id) {
      loadProductForEdit();
    }
  }, [isEdit, id, loadProductForEdit]);

  const handleSave = async () => {
    setLoading(true);
    // Validate required fields
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t('admin.nameRequired');
    }
    if (!formData.sku.trim()) {
      newErrors.sku = t('admin.skuRequired');
    }
    if (!formData.category.trim()) {
      newErrors.category = t('admin.categoryRequired');
    }
    if (!formData.price.trim()) {
      newErrors.price = t('admin.priceRequired');
    } else {
      const priceNum = parseFloat(formData.price);
      if (Number.isNaN(priceNum) || priceNum <= 0) {
        newErrors.price = t('admin.discountedPriceInvalid');
      }
    }
    if (formData.normalPrice.trim()) {
      const normalPriceNum = parseFloat(formData.normalPrice);
      const sellingPriceNum = parseFloat(formData.price);
      if (Number.isNaN(normalPriceNum) || normalPriceNum <= 0) {
        newErrors.normalPrice = t('admin.normalPriceInvalid');
      } else if (!Number.isNaN(sellingPriceNum) && normalPriceNum <= sellingPriceNum) {
        newErrors.normalPrice = t('admin.normalPriceHigherThanDiscounted');
      }
    }
    if (!formData.description.trim()) {
      newErrors.description = t('admin.descriptionRequired');
    }
    if (!formData.weight.trim()) {
      newErrors.weight = t('admin.weightRequired');
    } else {
      const weightNum = parseFloat(formData.weight);
      if (isNaN(weightNum) || weightNum <= 0) {
        newErrors.weight = t('admin.weightInvalid');
      }
    }
    if (!introImageFile && !introImagePreview) {
      newErrors.introImage = t('admin.introImageRequired');
    }
    if (Object.keys(newErrors).length > 0) {
      alert(Object.values(newErrors).join('\n'));
      setLoading(false);
      return;
    }

    let additionalImages = [];
    if (imageFiles.length > 0) {
      additionalImages = await uploadProductImages(imageFiles, id);
    } else if (imagePreviews.length > 0 && isEdit) {
      // If editing and no new images selected, keep existing
      additionalImages = imagePreviews;
    }

    let introImageUrl = introImagePreview;
    if (introImageFile) {
      introImageUrl = await uploadIntroImage(introImageFile, id);
    }

    // Prepare product data with proper type conversions
    const productData = {
      ...formData,
      weight: formData.weight.trim(),
      normalPrice: formData.normalPrice.trim(),
      price: formData.price.trim(),
      additionalImages,
      image: introImageUrl
    };

    if (isEdit) {
      // Update existing product
      const result = await updateProduct(id, productData);
      if (result.success) {
        alert(t('admin.productUpdated'));
        navigate('/admin-portal/products');
      } else {
        alert(t('admin.updateFailed', { error: result.error }));
      }
    } else {
      // Add new product
      const result = await addProduct(productData);
      if (result.success) {
        alert(t('admin.productAdded'));
        navigate('/admin-portal/products');
      } else {
        alert(t('admin.addFailed', { error: result.error }));
      }
    }
    setLoading(false);
  };

  const handleCancel = () => {
    navigate('/admin-portal/products');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'weight' || name === 'price' || name === 'normalPrice') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        processedValue = value;
      } else {
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const isEnabled = formData.status !== 'disable';

  const toggleStatus = () => {
    setFormData(prev => ({
      ...prev,
      status: prev.status === 'disable' ? 'enable' : 'disable'
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    setImageFiles(files);
    setImagePreviews(files.map(file => URL.createObjectURL(file)));
  };

  const handleIntroImageChange = (e) => {
    const file = e.target.files[0];
    setIntroImageFile(file);
    setIntroImagePreview(file ? URL.createObjectURL(file) : '');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="products" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50">
          <div className="max-w-[1280px] mx-auto p-4 md:p-6">
            <div className="mb-4">
              <button
                type="button"
                onClick={() => navigate('/admin-portal/products')}
                className="flex items-center gap-1 text-gray-500 text-xs uppercase tracking-wider font-semibold hover:text-primary transition-colors"
                disabled={loading}
              >
                <ArrowBackIcon className="!w-4 !h-4" />
                {t('admin.backToInventory')}
              </button>
            </div>

            <div className="mb-6 border-b-2 border-gray-200 pb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-primary">{t('admin.addEditTitle')}</h1>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
              className="space-y-6"
            >
              <div className="bg-white p-4 border border-gray-200 rounded">
                <h3 className="text-lg font-bold text-primary mb-4 border-b border-gray-200 pb-1">
                  {t('admin.basicInformation')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="product_name">
                      {t('admin.productName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="product_name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={fieldInputClass}
                      placeholder={t('admin.productNamePlaceholder')}
                      disabled={loading}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="sku">
                      {t('admin.sku')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="sku"
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleInputChange}
                      className={`${fieldInputClass} font-mono`}
                      placeholder={t('admin.skuPlaceholder')}
                      disabled={loading}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="category">
                      {t('admin.category')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleCategoryChange}
                      className={fieldSelectClass}
                      disabled={loading}
                    >
                      <option value="">{t('admin.selectCategory')}</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {getCategoryLabel(cat)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {getAvailableSubCategories().length > 0 && (
                    <div className="flex flex-col gap-1">
                      <label className={fieldLabelClass} htmlFor="sub_category">
                        {t('admin.subCategory')}
                      </label>
                      <select
                        id="sub_category"
                        name="subCategory"
                        value={formData.subCategory}
                        onChange={handleInputChange}
                        className={fieldSelectClass}
                        disabled={loading}
                      >
                        <option value="">{t('admin.selectSubCategory')}</option>
                        {getAvailableSubCategories().map((subCat) => (
                          <option key={subCat} value={subCat}>
                            {subCat}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between rounded border border-gray-200 p-3">
                  <div className="flex flex-col">
                    <span className={fieldLabelClass}>{t('admin.productStatus')}</span>
                    <span className="text-xs text-gray-500 mt-1">{t('admin.productStatusHint')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-semibold ${isEnabled ? 'text-primary' : 'text-gray-400'}`}>
                      {isEnabled ? t('admin.statusEnabled') : t('admin.statusDisabled')}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isEnabled}
                      onClick={toggleStatus}
                      disabled={loading}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                        isEnabled ? 'bg-primary' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                          isEnabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border border-gray-200 rounded">
                <h3 className="text-lg font-bold text-primary mb-4 border-b border-gray-200 pb-1">
                  {t('admin.pricingDetails')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="normal_price">
                      {t('admin.normalPrice')}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">
                        RM
                      </span>
                      <input
                        id="normal_price"
                        type="text"
                        inputMode="decimal"
                        name="normalPrice"
                        value={formData.normalPrice}
                        onChange={handleInputChange}
                        className={`${fieldInputClass} pl-10 font-mono`}
                        placeholder={t('admin.pricePlaceholder')}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="discounted_price">
                      {t('admin.discountedPrice')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-mono">
                        RM
                      </span>
                      <input
                        id="discounted_price"
                        type="text"
                        inputMode="decimal"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        className={`${fieldInputClass} pl-10 font-mono`}
                        placeholder={t('admin.pricePlaceholder')}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="weight">
                      {t('admin.weight')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="weight"
                      type="text"
                      name="weight"
                      value={formData.weight}
                      onChange={handleInputChange}
                      className={`${fieldInputClass} font-mono`}
                      placeholder={t('admin.weightPlaceholder')}
                      inputMode="decimal"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-1">
                  <label className={fieldLabelClass} htmlFor="description">
                    {t('admin.description')} <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-200 rounded bg-white overflow-hidden focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-colors">
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="5"
                      className="w-full p-3 border-none focus:ring-0 text-sm text-primary resize-y"
                      placeholder={t('admin.descriptionPlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="shopee_url">
                      {t('admin.shopeeUrl')}
                    </label>
                    <input
                      id="shopee_url"
                      type="url"
                      name="shopeeUrl"
                      value={formData.shopeeUrl}
                      onChange={handleInputChange}
                      className={fieldInputClass}
                      placeholder={t('admin.shopeeUrlPlaceholder')}
                      disabled={loading}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={fieldLabelClass} htmlFor="tiktok_url">
                      {t('admin.tiktokUrl')}
                    </label>
                    <input
                      id="tiktok_url"
                      type="url"
                      name="tiktokUrl"
                      value={formData.tiktokUrl}
                      onChange={handleInputChange}
                      className={fieldInputClass}
                      placeholder={t('admin.tiktokUrlPlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border border-gray-200 rounded">
                <h3 className="text-lg font-bold text-primary mb-4 border-b border-gray-200 pb-1">{t('admin.media')}</h3>

                <div className="flex flex-col gap-1 mb-6">
                  <label className={fieldLabelClass}>
                    {t('admin.introductionImage')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={introImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleIntroImageChange}
                    disabled={loading}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => introImageInputRef.current?.click()}
                    disabled={loading}
                    className="border-2 border-dashed border-gray-200 rounded p-8 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer group w-full"
                  >
                    <CloudUploadIcon className="!w-12 !h-12 text-gray-400 mb-2 group-hover:text-primary transition-colors" />
                    <p className="text-sm text-primary font-medium mb-1">{t('admin.dragDropImage')}</p>
                    <p className="text-sm text-gray-500">
                      {t('admin.or')}{' '}
                      <span className="text-primary font-medium">{t('admin.browseFiles')}</span>
                    </p>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mt-2">{t('admin.imageFormatHint')}</p>
                  </button>
                  {introImagePreview && (
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="aspect-square border border-gray-200 rounded bg-gray-100 overflow-hidden">
                        <img src={introImagePreview} alt={t('admin.introPreviewAlt')} className="w-full h-full object-cover" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className={fieldLabelClass}>{t('admin.productImagesMax')}</label>
                  <input
                    ref={productImagesInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    disabled={loading}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => productImagesInputRef.current?.click()}
                    disabled={loading}
                    className="border-2 border-dashed border-gray-200 rounded p-8 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer group w-full"
                  >
                    <CloudUploadIcon className="!w-12 !h-12 text-gray-400 mb-2 group-hover:text-primary transition-colors" />
                    <p className="text-sm text-primary font-medium mb-1">{t('admin.dragDropImages')}</p>
                    <p className="text-sm text-gray-500">
                      {t('admin.or')}{' '}
                      <span className="text-primary font-medium">{t('admin.browseFiles')}</span>
                    </p>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mt-2">{t('admin.imageFormatHint')}</p>
                  </button>

                  {imagePreviews.length > 0 ? (
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                      {imagePreviews.map((src, idx) => (
                        <div
                          key={idx}
                          className="aspect-square border border-gray-200 rounded bg-gray-100 overflow-hidden relative group"
                        >
                          <img src={src} alt={t('admin.previewAlt', { index: idx + 1 })} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="aspect-square border border-gray-200 rounded bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="text-gray-400" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border-2 border-primary text-primary text-sm font-bold rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-blue-900 transition-colors shadow-sm disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? t('admin.saving') : t('admin.saveProduct')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AddEditProducts;
